import { useQueryClient } from "@tanstack/react-query";
import { Form } from "antd";
import { useUpdateOfferMutation } from "api/TenantSlug/Offer/hooks.ts";
import { BaseFilter } from "components/UI/BaseFilter/BaseFilter.tsx";
import { FilterErrorsModal } from "components/UI/BaseFilter/FilterErrorsModal.tsx";
import { useFilterEditor } from "components/UI/BaseFilter/hooks/useFilterEditor.ts";
import { useFilterFormStructures } from "components/UI/BaseFilter/hooks/useFilterFormStructures.ts";
import { useUnsavedChangesGuard } from "components/UI/BaseFilter/hooks/useUnsavedChangesGuard.ts";
import { asRequestFilter } from "components/UI/BaseFilter/types/filterTree.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FormId } from "types/models.ts";

import OfferData = App.Data.OfferData;

interface OfferFilterEditFormProps {
    offer: OfferData;
}

export const OfferFilterEditForm = ({ offer }: OfferFilterEditFormProps) => {
    const [form] = Form.useForm();
    const formId = FormId.OfferFilterEditForm;
    const { t } = usePaldockTranslation("ui.offer-filter");
    const queryClient = useQueryClient();
    const [errorsOpen, setErrorsOpen] = useState(false);

    // Structures of the offer: those used by its API and those of its iframe forms
    const structureIds = useMemo(() => {
        const ids = [
            ...(offer.form_structures?.map((structure) => structure.id) ?? []),
            ...(offer.forms?.map((offerForm) => offerForm.form_structure_id) ?? []),
        ].filter((id): id is string => Boolean(id));
        return [...new Set(ids)];
    }, [offer.form_structures, offer.forms]);
    const { formStructures, fieldTags } = useFilterFormStructures(structureIds);

    const editor = useFilterEditor(offer.filter, "offer", fieldTags);
    useUnsavedChangesGuard(editor.isDirty);
    const updateOfferMutation = useUpdateOfferMutation(offer.id, {
        onError: () => toast.error(t("messages.save_error")),
    });

    const handleSubmit = async () => {
        // A filter the editor cannot show has an empty draft — saving it would wipe the filter on BE
        if (editor.unsupported) {
            toast.warning(t("unsupported.message"));
            return;
        }
        if (!editor.validate()) {
            setErrorsOpen(true);
            return;
        }

        try {
            const submittedTree = editor.toTree();
            await updateOfferMutation.mutateAsync({ filter: asRequestFilter(submittedTree) });
            editor.markSaved(submittedTree);
            await queryClient.invalidateQueries({ queryKey: ["getOffer", offer.id] });
            toast.success(t("messages.filters_saved"));
        } catch (error) {
            console.error("Error saving offer filter:", error);
        }
    };

    return (
        <Form form={form} id={formId} onFinish={handleSubmit} layout="vertical">
            <BaseFilter editor={editor} formStructures={formStructures} />
            <FilterErrorsModal errors={editor.errors} open={errorsOpen} onClose={() => setErrorsOpen(false)} />
        </Form>
    );
};
